import React from 'react';

import { PieChartOutlined } from '@ant-design/icons';
import { formatNumberWithDigits } from '@utils/common';
import { Skeleton } from 'antd';
import ReactECharts from 'echarts-for-react';

import './DashboardPieChart.scss';

/*

`data` & `percentage` example:
  data={[
    {
      value: 335,
      name: '直接访问',
      itemStyle: {
        color: '#5470C6',
      },
    },
    {
      value: 310,
      name: '邮件营销',
      itemStyle: {
        color: '#91CC75',
      },
    },
    {
      value: 234,
      name: '联盟广告',
      itemStyle: {
        color: '#EE6666',
      },
    },
  ]}
  percentage={20}
*/

export interface IData {
  value: number;
  name: string;
  itemStyle: {
    color: string;
  };
}
interface IDashboardPieChart {
  data: IData[];
  percentage: number;
  itemFullWidth?: boolean;
  isLoading?: boolean;
}

function safeParsePercentage(percentage: number) {
  if (Number.isNaN(percentage)) return 'N/A';
  if (percentage >= 100) return '100%';
  return `${formatNumberWithDigits(percentage, 1)}%`;
}

export default function DashboardPieChart({
  data,
  percentage,
  itemFullWidth,
  isLoading,
}: IDashboardPieChart) {
  if (isLoading)
    return (
      <div
        style={{
          height: '170px',
          width: '170px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Skeleton.Node active style={{ borderRadius: '50%' }}>
          <PieChartOutlined style={{ fontSize: 68, color: '#bfbfbf' }} />
        </Skeleton.Node>
      </div>
    );
  const option = {
    tooltip: {
      show: true, // Disables tooltip
      valueFormatter: (value: number) => {
        // get the sum of data, and the calculate the percentage
        const sum = data.reduce((acc, item) => acc + item.value, 0);
        // if (sum === 0) return `${formatNumberWithDigits(0, 1)}%`;
        const thisPercentage = (value / sum) * 100;
        if (Number.isNaN(thisPercentage)) return 'N/A';

        const formattedPercentage = formatNumberWithDigits(thisPercentage, 1);
        return thisPercentage >= 100 ? '100%' : `${formattedPercentage}%`;
      },
    },
    legend: {
      show: false,
    },
    series: [
      {
        type: 'pie',
        radius: ['75%', '100%'],
        avoidLabelOverlap: false,
        hoverAnimation: false, // Disable hover effects
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        labelLine: {
          show: false,
        },
        data,
      },
    ],
    graphic: {
      type: 'text',
      left: 'center',
      top: 'center',
      style: {
        text: safeParsePercentage(percentage),
        fontSize: '28px',
        fontWeight: 'bold',
        fill: '#2B3F60',
        cursor: 'default',
      },
    },
  };

  return (
    <div className="DashboardPieChart">
      <ReactECharts
        className="DashboardPieChart_chart"
        option={option}
        style={{ width: '140px', height: '140px' }}
        opts={{ renderer: 'svg' }}
      />
      <div className="DashboardPieChart_legend">
        {data.map((item) => (
          <div
            key={item.name}
            className="DashboardPieChart_legend_item"
            style={itemFullWidth ? { flex: 'auto', width: '100%' } : {}}
          >
            <div
              className="DashboardPieChart_legend_item_color"
              style={{ backgroundColor: item.itemStyle.color }}
            />
            <div className="DashboardPieChart_legend_item_name">
              {item.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
